import moment from 'moment/moment'
import { Fragment, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { BaseRecordView } from '../common/sections'
import { CustomFieldView } from '../items/CustomFieldView'
import { ContactAvatar } from './ContactAvatar'

const viewWillAppear = async ({ urlParams, owner }: PocketComponent) => {
    const subaccountID = urlParams.id
    if (!subaccountID) {
        return new RecordNotFoundError('Subaccount not found')
    }

    const dashboardDataResult = await RecordFactory.getInstance().fetchSubaccount(subaccountID)
    if (!dashboardDataResult.ok) {
        return new RecordNotFoundError('Subaccount not found')
    }
    const dashboardData = dashboardDataResult.value

    const subaccount = await RecordFactory.getInstance().loadSubaccount(subaccountID)
    if (!subaccount) {
        return new RecordNotFoundError('Subaccount not found')
    }

    const customFields = owner.id
        ? await RecordFactory.getInstance().loadSubaccountFields(owner.id, false)
        : []

    return { subaccount, dashboardData, customFields }
}

const render = ({ data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const navigate = useNavigate()

    const backHistoryEntry = findLastHistoryEntry({ excluding: /^\/subaccounts\/.*/ })

    const { subaccount, dashboardData, customFields } = data

    usePageTitle(subaccount.name ?? 'Subaccount')

    const otherDetailItems = (() => {
        const items: { id: string; title: string; content: ReactNode }[] = []

        if (subaccount.birthDay) {
            items.push({
                id: 'birthday',
                title: 'Birthday',
                content: moment(subaccount.birthDay).format('MMMM Do, YYYY'),
            })
        }

        if (subaccount.memo) {
            items.push({
                id: 'description',
                title: 'Description',
                content: subaccount.memo,
            })
        }

        for (const field of customFields) {
            const value = subaccount.fieldValues[field.name ?? '']
            if (value === undefined) {
                continue
            }

            items.push({
                id: field.id ?? '',
                title: field.label ?? '',
                content: <CustomFieldView field={field} value={value} />,
            })
        }

        return items
    })()

    return (
        <BaseRecordView
            title="Sub-account"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Clients',
                          onClick: () => navigate('/clients'),
                      }
            }
            headerButtons={[
                { title: 'Edit', onClick: () => navigate(`/subaccounts/${subaccount.id}/edit`) },
                {
                    title: 'View history',
                    onClick: () =>
                        navigate(
                            `/dashboard/user/${subaccount.client?.id}/subaccount/${subaccount.id}`
                        ),
                },
            ]}
        >
            <BaseRecordView.Container>
                <BaseRecordView.LeftColumn>
                    <Card size="sm">
                        <div className="flex flex-col items-center justify-center">
                            <ContactAvatar
                                user={subaccount}
                                className="size-[116px] text-[52px]"
                                iconSVGClassName="size-14"
                            />

                            <Heading size="sm" className="mt-6 mb-1 text-center">
                                {subaccount.name}
                            </Heading>

                            <Paragraph size="lg" className="font-light text-gray-600">
                                {subaccount.labelString}
                            </Paragraph>

                            <Divider className="my-6" />

                            <div className="flex w-full flex-col items-stretch justify-start gap-y-6">
                                <BaseRecordView.IconField
                                    icon="dollar.large"
                                    title={
                                        dashboardData.income !== undefined
                                            ? currencyFormat(dashboardData.income)
                                            : '-'
                                    }
                                    subtitle="Income"
                                />

                                <BaseRecordView.IconField
                                    icon="calendar.large"
                                    title={
                                        dashboardData.last_date
                                            ? moment(dashboardData.last_date).format('MMM Do, YYYY')
                                            : dashboardData.next_date
                                              ? moment(dashboardData.next_date).format(
                                                    'MMM Do, YYYY'
                                                )
                                              : '-'
                                    }
                                    subtitle={
                                        dashboardData.last_date
                                            ? 'Last appointment'
                                            : 'Next appointment'
                                    }
                                />

                                <BaseRecordView.IconField
                                    icon="coins2.large"
                                    title={
                                        dashboardData.balance !== undefined
                                            ? currencyFormat(dashboardData.balance)
                                            : '-'
                                    }
                                    subtitle="Balance"
                                />

                                {subaccount.client !== undefined && (
                                    <BaseRecordView.IconField
                                        icon={<ContactAvatar user={subaccount.client} />}
                                        title={subaccount.client.name ?? ''}
                                        subtitle="Client"
                                        href={`/clients/${subaccount.client.id}`}
                                    />
                                )}

                                {subaccount.employee !== undefined && (
                                    <BaseRecordView.IconField
                                        icon={<ContactAvatar user={subaccount.employee} />}
                                        title={subaccount.employee.name ?? ''}
                                        subtitle="Team member"
                                        href={`/team-members/${subaccount.employee.id}`}
                                    />
                                )}
                            </div>
                        </div>
                    </Card>

                    {subaccount.location !== undefined &&
                        (subaccount.location ?? '').length > 0 &&
                        subaccount.latitude !== undefined &&
                        subaccount.longitude !== undefined && (
                            <BaseRecordView.LocationSection
                                location={subaccount.location}
                                latLong={{
                                    latitude: subaccount.latitude,
                                    longitude: subaccount.longitude,
                                }}
                            />
                        )}

                    <Card title="Date added" size="sm">
                        <Paragraph size="md" className="mt-6 text-gray-600">
                            {moment(subaccount.createdDate).format('MMMM Do, YYYY')}
                        </Paragraph>
                    </Card>
                </BaseRecordView.LeftColumn>

                <BaseRecordView.RightColumn>
                    <BaseRecordView.NotesSection target={{ type: 'subaccount', subaccount }} />

                    {otherDetailItems.length > 0 && (
                        <Card title="Other details">
                            <div className="mt-8 flex flex-col items-stretch justify-start">
                                {otherDetailItems.map(
                                    ({ id, title, content }, otherDetailItemIndex) => (
                                        <Fragment key={id}>
                                            <div className="flex flex-col items-stretch justify-center gap-y-2">
                                                <Label size="lg">{title}</Label>
                                                <div className="text-paragraph-md text-gray-600">
                                                    {content}
                                                </div>
                                            </div>

                                            {otherDetailItemIndex < otherDetailItems.length - 1 && (
                                                <Divider className="my-4" />
                                            )}
                                        </Fragment>
                                    )
                                )}
                            </div>
                        </Card>
                    )}
                </BaseRecordView.RightColumn>
            </BaseRecordView.Container>
        </BaseRecordView>
    )
}

export const SubaccountPage = usePSComponent(render, viewWillAppear)
